// Score colour palette and the raw per-area values shown in tooltips and listing cards.
// Scores themselves come from neighbourhoodScoring.js.

export const red = '#ff3627';
export const redBright = '#dc6f2b';
export const yellow = '#e8bd5f';
export const greenBright = '#79e39f';
export const green = '#51c987';
export const blueBright = '#75bae8';
export const blue = '#4a96d0';
export const pinkBright = '#f48ec2';
export const pink = '#f44b96';
export const purpleBright = '#846fda';
export const purple = '#5a4cd0';

const DISCRETE_COLORS = {
    1: red, 1.5: redBright, 2: yellow, 2.5: greenBright, 3: green, 3.5: blueBright,
    4: blue, 4.5: pinkBright, 5: pink, 5.5: purpleBright, 6: purple,
};

// "Värmekarta": the same 11 steps from deep red (1) to white (6).
const HEAT_COLORS = {
    1: '#9e0a12', 1.5: '#c11d1d', 2: '#dc3a2b', 2.5: '#eb5c42', 3: '#f37f5f', 3.5: '#f8a084',
    4: '#fbbda6', 4.5: '#fdd4c3', 5: '#fee5da', 5.5: '#fff0ea', 6: '#fffaf7',
};

const COLOUR_SCHEMES = { discrete: DISCRETE_COLORS, heat: HEAT_COLORS };
const COLOUR_SCHEME_COOKIE = 'colour_scheme';

// Only the regular colour scale is offered now (the heat map was removed), so always use 'discrete'
// regardless of any older saved `colour_scheme` cookie.
const readSchemeCookie = () => 'discrete';

// Whole-star colours (1, 2, 3, 4, 5, 6) get a stronger fill than the half steps, so neighbouring scores
// are easier to tell apart on the map.
const WHOLE_STAR_FILL_OPACITY_BOOST = 0.15;
const WHOLE_STAR_COLORS = new Set(
    Object.values(COLOUR_SCHEMES).flatMap((palette) => [1, 2, 3, 4, 5, 6].map((score) => palette[score])),
);
export const areaFillOpacity = (color, opacity) => (
    WHOLE_STAR_COLORS.has(color) ? Math.min(1, opacity + WHOLE_STAR_FILL_OPACITY_BOOST) : opacity
);

// Score -> hex in the active colour scheme. One shared object, changed in place by setColourScheme, so
// every module that reads it (map, legend, cards, filter bars) follows; the views recompute their colour
// cache when the scheme changes. colorToScore knows both schemes' colours.
export const scoreToColor = { ...COLOUR_SCHEMES[readSchemeCookie()] };

export const colorToScore = Object.fromEntries(
    Object.values(COLOUR_SCHEMES).flatMap((palette) => Object.entries(palette).map(([score, hex]) => [hex, Number(score)])),
);

export const COLOUR_SCHEME_STEPS = { discrete: DISCRETE_COLORS, heat: HEAT_COLORS };

let activeScheme = readSchemeCookie();
const schemeListeners = new Set();

// body.colour-heat lets CSS give near-white score swatches an outline.
const markBody = (name) => {
    if (typeof document !== 'undefined' && document.body) document.body.classList.toggle('colour-heat', name === 'heat');
};
if (typeof document !== 'undefined') {
    if (document.body) markBody(activeScheme);
    else document.addEventListener('DOMContentLoaded', () => markBody(activeScheme), { once: true });
}

export const getColourScheme = () => activeScheme;

export const subscribeColourScheme = (listener) => {
    schemeListeners.add(listener);
    return () => schemeListeners.delete(listener);
};

export const setColourScheme = (name) => {
    if (!COLOUR_SCHEMES[name] || name === activeScheme) return;
    activeScheme = name;
    Object.assign(scoreToColor, COLOUR_SCHEMES[name]);
    if (typeof document !== 'undefined') {
        document.cookie = `${COLOUR_SCHEME_COOKIE}=${name}; path=/; max-age=31536000; samesite=lax`;
    }
    markBody(name);
    schemeListeners.forEach((listener) => listener(name));
};

// Text or an icon drawn in a score colour: the colour itself, or a darker ink when the colour is too
// light to read on white (the heat scheme's top steps).
const luminance = (hex) => {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return 0;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
    return 0.299 * r + 0.587 * g + 0.114 * b;
};
export const scoreInkColor = (hex) => (luminance(hex) > 0.75 ? '#b3574a' : hex);
// Dark or white for text on a score-coloured background.
export const scoreTextOn = (hex) => (luminance(hex) > 0.6 ? '#13241f' : '#ffffff');

// "<metric>_<regsoCode>" → display value. The views merge neighbourhood scores (`combined_`) over this.
export const areaStats = {};

export function fillAreaStats(completeData) {
    if (completeData == null) return;

    const unemploymentRawValues = Object.values(completeData)
        .map((area) => Number(area.unemployment))
        .filter((value) => Number.isFinite(value));
    // Unemployment comes either as a fraction or as a percentage
    const unemploymentScale = unemploymentRawValues.length && Math.max(...unemploymentRawValues) <= 1 ? 100 : 1;

    for (const key in completeData) {
        const area = completeData[key];
        areaStats[`netInc_${key}`] = area.netInc;
        // 0 or -1 means too few sales (see the backend's area_sqm_prices.py)
        areaStats[`sqm_price_apartment_${key}`] = Math.round(area.apartmentAvgSqmPrice);
        areaStats[`sqm_price_house_${key}`] = Math.round(area.houseAvgSqmPrice);

        const total = area.rentAp + area.ownedAp + area.house;
        areaStats[`apartment_${key}`] = Math.round(((area.ownedAp + area.house) / total) * 100) / 100;
        areaStats[`education_${key}`] = Math.round(area.higherEduPop * 100);

        const unemploymentRaw = Number(area.unemployment);
        areaStats[`unemployment_${key}`] = Number.isFinite(unemploymentRaw)
            ? Math.round(unemploymentRaw * unemploymentScale * 10) / 10
            : null;
    }
}
